import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { useSlidingPill } from '@apps/web/hooks/use_sliding_pill';
import { cn } from '@apps/web/lib/utils';

// A tab strip scrolls rather than clipping once its labels no longer fit.
// Without this a row of `whitespace-nowrap` triggers is simply wider than a
// phone screen, and every ancestor in the app shell is `overflow-hidden`, so
// the trailing tabs cannot be reached at all. Shared with the hand-rolled
// `role="tablist"` strips, which are not Radix tabs but owe the same promise.
export const SCROLLABLE_TAB_STRIP_CLASSNAME =
    'scrollbar-none max-w-full overflow-x-auto overscroll-x-contain';

// Mirrors the Radix root's active value so the list can position (and slide)
// the shared active-pill without reaching into Radix internals. Null when the
// sliding indicator is opted out or triggers render outside <Tabs>.
const TabsActiveValueContext = React.createContext<string | undefined | null>(
    null,
);

function Tabs({
    className,
    value,
    defaultValue,
    onValueChange,
    slidingIndicator = true,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
    // Set false when triggers restyle their active state (custom background
    // or border) — the sliding pill would paint behind that styling and the
    // slide adds nothing.
    slidingIndicator?: boolean;
}) {
    const [internalValue, setInternalValue] = React.useState(defaultValue);

    return (
        <TabsActiveValueContext.Provider
            value={slidingIndicator ? (value ?? internalValue) : null}
        >
            <TabsPrimitive.Root
                data-slot="tabs"
                className={cn('flex flex-col gap-2', className)}
                value={value}
                defaultValue={defaultValue}
                onValueChange={(next) => {
                    setInternalValue(next);
                    onValueChange?.(next);
                }}
                {...props}
            />
        </TabsActiveValueContext.Provider>
    );
}

function TabsList({
    className,
    children,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
    const activeValue = React.useContext(TabsActiveValueContext);
    const trackRef = React.useRef<HTMLDivElement>(null);
    const pill = useSlidingPill(
        trackRef,
        '[data-state="active"]',
        activeValue ?? undefined,
    );

    return (
        <TabsPrimitive.List
            ref={trackRef}
            data-slot="tabs-list"
            data-phone-scroll="intended"
            className={cn(
                // `isolate` scopes the pill's -z-10 so it paints behind the
                // trigger labels but not the page.
                //
                // Desktop is unchanged by the scroll contract: a scroll
                // container's min-content size along the scroll axis is zero,
                // so `w-fit` still resolves to max-content whenever the row
                // fits. `justify-start` matters only when scrolling (centred
                // overflow puts the leading tab out of reach); a hugging row
                // has no free space to justify.
                SCROLLABLE_TAB_STRIP_CLASSNAME,
                'bg-muted text-muted-foreground relative isolate inline-flex h-9 w-fit items-center justify-start rounded-lg p-[3px] pointer-coarse:h-auto',
                className,
            )}
            {...props}
        >
            {activeValue !== null && pill !== null && (
                <span
                    aria-hidden="true"
                    className="bg-background dark:border-input dark:bg-input/30 absolute -z-10 rounded-md border border-transparent transition-[left,top,width,height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
                    style={pill}
                />
            )}
            {children}
        </TabsPrimitive.List>
    );
}

function TabsTrigger({
    className,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
    const activeValue = React.useContext(TabsActiveValueContext);

    return (
        <TabsPrimitive.Trigger
            data-slot="tabs-trigger"
            className={cn(
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:text-foreground text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 pointer-coarse:min-h-11",
                // Never shrink: a min-width would let `flex-1` squeeze a label
                // under its neighbour, and the row scrolls instead.
                'pointer-coarse:flex-[1_0_2.75rem]',
                // Static active styling when the sliding pill is opted out or
                // the trigger renders outside <Tabs>.
                activeValue === null &&
                    'data-[state=active]:bg-background dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30',
                className,
            )}
            {...props}
        />
    );
}

function TabsContent({
    className,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
    return (
        <TabsPrimitive.Content
            data-slot="tabs-content"
            className={cn(
                'flex-1 outline-none data-[state=inactive]:hidden',
                className,
            )}
            {...props}
        />
    );
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
